import React, { Dispatch, useCallback, useEffect } from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    getDocumentMetadata,
    ProjectSidepanel,
} from '../../../redux/project/sliceProjectSidepanel';
import { IAppReduxStore } from '../../../redux/store';
import { getFileInfo } from '../../../util/FileUtils';
import FileTile from '../FileTile/FileTile';
import PlaceholderCard from './Components/PlaceholderCard';
import LoadingData from './Components/LoadingData';

/**
 * Sidepanel that shows information on the files associated with a given document.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'sidepanel' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId, areaName and viewName from the react router. */
    const { replicaId, areaName, viewName } = useParams();

    /** Get relevant values from Redux store. */
    const {
        data,
        loading,
        targetDocId: universalId,
    } = useSelector((store: IAppReduxStore) => store.projectSidepanel);

    /** Access the document's file infomation from the Redux store. */
    const sidepanelData = data[ProjectSidepanel.Files];

    /**
     * Effect that fetches the relevant document data for displaying in the sidepanel.
     */
    useEffect(() => {
        !data[ProjectSidepanel.Files] &&
            universalId &&
            dispatch(
                getDocumentMetadata({
                    replicaId,
                    viewName: `${areaName}.${viewName}`,
                    universalId,
                    fields: ['File', 'FileDataModel'],
                    tab: ProjectSidepanel.Files,
                }),
            );
    }, [universalId, data]);

    /**
     * Creates the list of FileTile components for the document's files.
     * @param title -> Title text to show above the file list.
     * @param list -> File attachment information.
     */
    const createFileList = useCallback((key: 'File' | 'FileDataModel') => {
            const list: string | string[] = sidepanelData?.[key];
            const hasFiles = list?.length > 0;

            /** Convert the file information strings into FileTile components. */
            const fileList = () =>
                (typeof list === 'string' ? [list] : list).map((x, i) => (
                    <FileTile
                        key={x}
                        index={i}
                        sidepanelData={sidepanelData}
                        type={key}
                        fileData={getFileInfo(x, true)}
                    />
                ));

            return (
                <Stack spacing={1}>
                    <Typography
                        variant='h6'
                        sx={{
                            fontSize: 16,
                            py: 1,
                        }}
                    >
                        {t(key)}
                    </Typography>
                    {loading && <LoadingData />}
                    {!loading && !hasFiles && <PlaceholderCard translationKey={`No${key}Placeholder`} />}
                    {hasFiles && <Stack spacing={1}>{fileList()}</Stack>}
                </Stack>
            );
        },
        [loading, sidepanelData],
    );

    return (
        <Stack sx={{ overflow: 'auto', mx: 1 }} spacing={2}>
            {createFileList('File')}
            {createFileList('FileDataModel')}
        </Stack>
    );
};
